import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Toast from '../../Shared/Toast';
import OffseasonNotice from '../../Shared/OffseasonNotice';
import { loadBattlePrefs, saveBattlePrefs } from '../../../utils/battlePreferences';
import { useMercenary } from '../../../contexts/MercenaryContext';
import Tooltip from '../ItemTooltip';
import styles from './BeastBattle.module.css';

interface LootItem {
  itemId?: number;
  itemName: string;
  itemType: string;
  dropType: string;
  minQuantity: number;
  maxQuantity: number;
}

interface Beast {
  id: number;
  name: string;
  description: string;
  level: number;
  minRequiredLevel: number;
  maxRequiredLevel: number;
  
  // Stat brackets instead of exact values
  healthBracket: string;
  strengthBracket: string;
  enduranceBracket: string;
  initiativeBracket: string;
  dodgeBracket: string;

  // Server-wide records
  highestHitRecorded: number;
  highestCritRecorded: number;
  
  // Sanitized loot (no drop percentages)
  loot: LootItem[];
}

interface LocationState {
  beast: Beast;
}

const BeastBattle: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { beast } = location.state as LocationState;
  const { mercenaryId, setMercenary } = useMercenary();

  const [surrenderAt, setSurrenderAt] = useState<number>(50);

  const [strategy, setStrategy] = useState<string>('Normal');

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  useEffect(() => {
    if (!mercenaryId) return;
    const prefs = loadBattlePrefs(mercenaryId, 'beast-battle', {
      surrenderAt: 50,
      strategy: 'Normal',
    });
    setSurrenderAt(prefs.surrenderAt as number);
    setStrategy(prefs.strategy as string);
  }, [mercenaryId]);

  useEffect(() => {
    if (!mercenaryId) return;
    saveBattlePrefs(mercenaryId, 'beast-battle', {
      surrenderAt,
      strategy,
    });
  }, [mercenaryId, surrenderAt, strategy]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mercenaryId) return;
    setIsLoading(true);

    try {
      const response = await axios.post(
        '/api/battle/battle',
        {
          teamOne: [
            { mercenaryId, strategy, surrenderHP: surrenderAt / 100 },
          ],
          teamTwo: [
            { beastId: beast.id, strategy: 'Normal', surrenderHP: 0, type: 'beast' },
          ],
          deathMatch: false,
          playerMercenaryId: mercenaryId,
          title: `Beast Battle - ${beast.name}`,
          battleType: 'Beast Battle',
        },
        { withCredentials: true }
      );
      // Update mercenary context immediately from HTTP response (don't rely on socket)
      if (response.data.updatedStats) {
        setMercenary((prev: any) => ({ ...prev, ...response.data.updatedStats }));
      }
      navigate(`/battle-result/${response.data.battleId}`);
    } catch (err: any) {
      console.error('Error initiating battle:', err);
      displayToast(err.response?.data?.message || 'Failed to initiate battle. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const displayToast = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
  };

  return (
    <OffseasonNotice feature="beast battles">
      <div className={styles.page}>
        {toastMessage && (
          <Toast
            message={toastMessage}
            type={toastType}
            duration={3000}
            onClose={() => setToastMessage(null)}
          />
        )}

        <section className={styles.hero}>
          <div>
            <h1 className={styles.title}>{beast.name}</h1>
            <p className={styles.subtitle}>{beast.description}</p>
            <div className={styles.badges}>
              <span className={styles.badge}>Level {beast.level}</span>
              <span className={styles.badge}>Range {beast.minRequiredLevel}-{beast.maxRequiredLevel}</span>
            </div>
          </div>
        </section>

        <div className={styles.grid}>
          <div className={styles.beastPanel}>
            <div className={styles.beastHeader}>
              <div className={styles.imagePlaceholder}>
                <span>IMG</span>
              </div>
              <table className={styles.statsTable}>
                <tbody>
                  <tr>
                    <td className={styles.statLabel}>Health</td>
                    <td className={styles.statValue}>{beast.healthBracket}</td>
                  </tr>
                  <tr>
                    <td className={styles.statLabel}>Strength</td>
                    <td className={styles.statValue}>{beast.strengthBracket}</td>
                  </tr>
                  <tr>
                    <td className={styles.statLabel}>Endurance</td>
                    <td className={styles.statValue}>{beast.enduranceBracket}</td>
                  </tr>
                  <tr>
                    <td className={styles.statLabel}>Initiative</td>
                    <td className={styles.statValue}>{beast.initiativeBracket}</td>
                  </tr>
                  <tr>
                    <td className={styles.statLabel}>Dodge</td>
                    <td className={styles.statValue}>{beast.dodgeBracket}</td>
                  </tr>
                  {beast.highestHitRecorded > 0 && (
                    <tr className={styles.statRowRecord}>
                      <td className={styles.statLabel}>Highest Hit</td>
                      <td className={styles.statValue}>{beast.highestHitRecorded}</td>
                    </tr>
                  )}
                  {beast.highestCritRecorded > 0 && (
                    <tr className={styles.statRowRecord}>
                      <td className={styles.statLabel}>Highest Crit</td>
                      <td className={styles.statValue}>{beast.highestCritRecorded}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className={styles.lootSection}>
              <p className={styles.lootTitle}>Known Spoils</p>
              {beast.loot && beast.loot.length > 0 ? (
                <div className={styles.lootList}>
                  {beast.loot.map((item, idx) => (
                    <div key={idx} className={styles.lootItem}>
                      {item.itemId ? (
                        <Tooltip itemId={item.itemId}>
                          <span>{item.itemName}</span>
                        </Tooltip>
                      ) : (
                        <span>{item.itemName}</span>
                      )}
                      {item.minQuantity !== item.maxQuantity && (
                        <span className={styles.lootQty}>{item.minQuantity}-{item.maxQuantity}</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className={styles.unknownLoot}>Unknown</p>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit} className={styles.controlsPanel}>
            <div className={styles.controlGroup}>
              <label className={styles.label}>Battle Strategy</label>
              <select
                className={styles.select}
                value={strategy}
                onChange={(e) => setStrategy(e.target.value)}
                required
              >
                <optgroup label="Balanced">
                  <option value="Normal">Normal</option>
                  <option value="Normal: Light Attacks">Normal: Light Attacks</option>
                  <option value="Normal: Heavy Attacks">Normal: Heavy Attacks</option>
                </optgroup>
                <optgroup label="Aggressive">
                  <option value="Offensive">Offensive</option>
                  <option value="Offensive: Light Attacks">Offensive: Light Attacks</option>
                  <option value="Offensive: Heavy Attacks">Offensive: Heavy Attacks</option>
                </optgroup>
                <optgroup label="Cautious">
                  <option value="Defensive">Defensive</option>
                  <option value="Defensive: Light Attacks">Defensive: Light Attacks</option>
                  <option value="Defensive: Heavy Attacks">Defensive: Heavy Attacks</option>
                </optgroup>
                <optgroup label="Reckless">
                  <option value="Berserk">Berserk</option>
                  <option value="Berserk: Light Attacks">Berserk: Light Attacks</option>
                  <option value="Berserk: Heavy Attacks">Berserk: Heavy Attacks</option>
                </optgroup>
              </select>
            </div>

            <div className={styles.controlGroup}>
              <label className={styles.label}>Surrender Threshold: {surrenderAt}% HP</label>
              <input
                type="range"
                min={0}
                max={90}
                step={10}
                value={surrenderAt}
                onChange={(e) => setSurrenderAt(parseInt(e.target.value))}
                className={styles.slider}
                style={{ '--val': `${Math.round((surrenderAt / 90) * 100)}%` } as React.CSSProperties}
              />
              <div className={styles.sliderLabels}>
                <span>Death (0%)</span>
                <span>Cautious (90%)</span>
              </div>
            </div>

            <button type="submit" className={styles.battleButton} disabled={isLoading}>
              {isLoading ? 'Calling the beast...' : 'Start Battle'}
            </button>
          </form>
        </div>
      </div>
    </OffseasonNotice>
  );
};

export default BeastBattle;
